import { ChartData } from "chart.js";
import groupReportData from "features/reports/SimpleReport/groupReportData";
import useSettings from "features/settings/useSettings";
import { createContext, useContext, useMemo } from "react";
import {
  CoordinatorReport2DataContextType,
  useCoordinatorReport2DataContext,
} from "./useCoordinatorReport2Data";

export type CoordinatorReport2ContextType = {
  pivot: ChartData;
  params: any;
  setParams: (params: any) => void;
  load: () => Promise<void>;
  loading: boolean;
  firstLoaded: boolean;
  streetTotals: { [label: string]: number };
  streetGrandTotal: number;
} & Omit<CoordinatorReport2DataContextType, "data">;

export const CoordinatorReport2Context =
  createContext<CoordinatorReport2ContextType | null>(null);

export default function useCoordinatorReport2() {
  const { data, filter, setFilter, load, loading, firstLoaded } =
    useCoordinatorReport2DataContext();
  const [params, setParams] = useSettings({ key: "simpleReport" });

  const pivot = useMemo(
    () =>
      groupReportData(
        data as any,
        "program",
        filter.from,
        filter.to,
        "guests",
        params.show_canceled,
      ),
    [params.show_canceled, data],
  );

  const { streetTotals, streetGrandTotal } = useMemo(() => {
    const totals: { [label: string]: number } = {};
    let grandTotal = 0;
    data.forEach(entry => {
      const label = entry.program;
      const val = entry.street_guests || 0;
      totals[label] = (totals[label] || 0) + val;
      grandTotal += val;
    });
    return { streetTotals: totals, streetGrandTotal: grandTotal };
  }, [data]);

  return {
    pivot: pivot as any,
    filter,
    setFilter,
    params,
    setParams,
    load,
    loading,
    firstLoaded,
    streetTotals,
    streetGrandTotal,
  };
}

export function useCoordinatorReport2Context() {
  const context = useContext(CoordinatorReport2Context);
  if (!context) {
    throw Error(
      "useCoordinatorReport2Context cannot be used outside of CoordinatorReport2Provider.",
    );
  }
  return context;
}
